/**
 * Seeded randomness.
 *
 * Three places need the same generator and for the same reason: the terrain, the scatter, and the muck
 * thrown off the wheels all have to come out identical on the way home as on the way out. One seed in,
 * one world out.
 *
 * It lived privately inside `heightfield.ts` and was copied by hand into the drive screen, with a comment
 * apologising for it. The apology was aimed at the wrong thing: exporting a PRNG from the module whose
 * whole job is determinism does invite somebody to reach for it casually, but a module that is *only* a
 * PRNG has no such confusion to offer.
 *
 * Pure, as everything in core is: no clock, no `Math.random`.
 */

/**
 * mulberry32: small, fast, well-distributed, and deterministic from a single integer seed.
 *
 * Not cryptographic and not trying to be. What matters here is that the same integer always produces the
 * same sequence, on every device and every browser, forever - which rules out anything that might be
 * implemented differently by an engine.
 */
export function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Derive one seed from another plus a label.
 *
 * So the scatter, the props and the spray can each have their own stream from one route seed without
 * consuming each other's numbers. Without this, adding one tree changes where every boulder goes - which
 * makes a route impossible to tune and a bug impossible to reproduce.
 */
export function deriveSeed(seed: number, label: string): number {
  let hash = seed | 0;
  for (let i = 0; i < label.length; i++) {
    hash = (Math.imul(hash ^ label.charCodeAt(i), 0x01000193) | 0) >>> 0;
  }
  return hash | 0;
}

/** A number between `min` and `max`. */
export function between(rand: () => number, min: number, max: number): number {
  return min + (max - min) * rand();
}

/** One of `values`. Never undefined for a non-empty list, which saves every caller a fallback. */
export function oneOf<T>(rand: () => number, values: readonly T[]): T | undefined {
  if (values.length === 0) return undefined;
  return values[Math.min(values.length - 1, Math.floor(rand() * values.length))];
}
